import { draftMoney, garmentDiscount, wearerLabel, type BookingDraft } from './draft';

/**
 * The bag — everyone in the party with what they are wearing, as photo rows, and the running total
 * — which the phone pins to the foot of the booking and the website draws as its summary panel. The
 * proposal's "a bag that never leaves the screen", worded once for both.
 */

/** One garment in the bag. */
export interface BagLine {
  unitId: number;
  /** The design's name — the row's heading. */
  name: string;
  serialCode: string;
  referenceNumber?: string | null;
  imageUrl: string | null;
  /** Picks the outline drawn when there is no picture. */
  categoryName: string;
  /** What this garment comes to after its own discount; null when nothing prices it. */
  charged: number | null;
  /** True when a discount of its own came off. */
  discounted: boolean;
}

/** One person in the bag. */
export interface BagPerson {
  key: string;
  name: string;
  relation: string;
  lines: BagLine[];
  /** Their garments after each one's own discount — the booking's discount is the bag's, not theirs. */
  subtotal: number;
}

export interface Bag {
  people: BagPerson[];
  garments: number;
  /** "2 garments · 1 person", or "Empty" before anything is picked. */
  headline: string;
  /** The rental after both discounts — the figure the booking will be for. */
  total: number;
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

export function bagOf(draft: BookingDraft): Bag {
  const people = draft.wearers.map((wearer): BagPerson => {
    const lines = wearer.garments.map((garment): BagLine => {
      const off = garmentDiscount(garment);

      return {
        unitId: garment.unitId,
        name: garment.designName ?? garment.itemName,
        serialCode: garment.serialCode,
        ...(garment.referenceNumber ? { referenceNumber: garment.referenceNumber } : {}),
        imageUrl: garment.imageUrl ?? null,
        categoryName: garment.categoryName ?? '',
        charged: garment.rate === null ? null : Math.max(0, garment.rate - off),
        discounted: off > 0,
      };
    });

    return {
      key: wearer.key,
      name: wearerLabel(wearer),
      relation: wearer.relation,
      lines,
      subtotal: lines.reduce((sum, line) => sum + (line.charged ?? 0), 0),
    };
  });

  const garments = people.reduce((sum, person) => sum + person.lines.length, 0);
  const wearing = people.filter((person) => person.lines.length > 0).length;

  return {
    people,
    garments,
    headline:
      garments === 0
        ? 'Empty'
        : `${plural(garments, 'garment', 'garments')} · ${plural(wearing, 'person', 'people')}`,
    total: draftMoney(draft).netRental,
  };
}
